import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { useCallback, useState } from 'react';

import { useStyles } from '../DatasetAnnotationsTab.style.js';

export interface DownloadAnnotationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultVersion: string;
  onConfirm: (version: string, annotationSetName: string) => void;
  isDownloading: boolean;
  downloadError: string | null;
}

export function DownloadAnnotationDialog({
  open,
  onOpenChange,
  defaultVersion,
  onConfirm,
  isDownloading,
  downloadError,
}: DownloadAnnotationDialogProps) {
  const styles = useStyles();

  const [version, setVersion] = useState(defaultVersion);
  const [annotationSetName, setAnnotationSetName] = useState('');

  const resetForm = useCallback(() => {
    setVersion(defaultVersion);
    setAnnotationSetName('');
  }, [defaultVersion]);

  const canConfirm =
    version.trim().length > 0 && annotationSetName.trim().length > 0;

  const handleConfirm = useCallback(() => {
    if (!canConfirm) return;
    onConfirm(version.trim(), annotationSetName.trim());
  }, [canConfirm, version, annotationSetName, onConfirm]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        onOpenChange(data.open);
        if (!data.open) resetForm();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Download Annotation Data</DialogTitle>
          <DialogContent>
            <div className={styles.dialogForm}>
              <Field label="Annotation set name" required>
                <Input
                  value={annotationSetName}
                  onChange={(_e, d) => setAnnotationSetName(d.value)}
                  placeholder="e.g. my-annotation-set"
                />
              </Field>
              <Field label="Version" required>
                <Input
                  value={version}
                  onChange={(_, d) => setVersion(d.value)}
                  placeholder="draft or version number"
                />
              </Field>
              {downloadError !== null && (
                <Text size={200} className={styles.errorState}>
                  {downloadError}
                </Text>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={isDownloading}
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!canConfirm || isDownloading}
              icon={isDownloading ? <Spinner size="tiny" /> : undefined}
              onClick={handleConfirm}
            >
              {isDownloading ? 'Downloading…' : 'Download'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
